import React, { useEffect, useState } from 'react';
import { TextInput } from '../../../shared/components/TextInput';
import { Textarea } from '../../../shared/components/Textarea';
import { validateMovimentacao } from '../validation/recrutamentoForms';
import { RecrutamentoModal } from './RecrutamentoModal';

export type MovimentacaoTipo = 'avancar' | 'retornar' | 'aprovar' | 'reprovar' | 'banco_talentos';

interface MovimentarModalProps {
  open: boolean;
  tipo: MovimentacaoTipo;
  candidato: string;
  etapaAtual: string;
  etapaDestino?: string;
  loading?: boolean;
  error?: string | null;
  onCancel: () => void;
  onConfirm: (input: { parecer?: string; score?: number }) => void;
}

const TITULOS: Record<MovimentacaoTipo, string> = {
  avancar: 'Avançar etapa',
  retornar: 'Retornar à etapa anterior',
  aprovar: 'Aprovar candidato',
  reprovar: 'Reprovar candidato',
  banco_talentos: 'Enviar ao banco de talentos',
};

/** Parecer obrigatório: retorno e reprovação, como o servidor exige. */
const PARECER_OBRIGATORIO: MovimentacaoTipo[] = ['retornar', 'reprovar'];

/** Nota faz sentido só nas transições de avaliação. */
const ACEITA_NOTA: MovimentacaoTipo[] = ['avancar', 'aprovar'];

/**
 * Diálogo de movimentação do candidato.
 *
 * O painel é kanban, mas a movimentação passa por aqui em vez de
 * arrastar-e-soltar: a transição exige parecer em vários casos e é
 * validada no servidor, então um gesto que "já move" mentiria sobre o
 * que aconteceu.
 */
export function MovimentarModal({
  open,
  tipo,
  candidato,
  etapaAtual,
  etapaDestino,
  loading,
  error,
  onCancel,
  onConfirm,
}: MovimentarModalProps) {
  const [parecer, setParecer] = useState('');
  const [score, setScore] = useState('');
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setParecer('');
      setScore('');
      setTouched(false);
    }
  }, [open, tipo]);

  const parecerObrigatorio = PARECER_OBRIGATORIO.includes(tipo);
  const notaNumerica = score.trim() === '' ? undefined : Number(score);
  const erros = validateMovimentacao(tipo, { parecer, score: notaNumerica });
  const invalido = Object.keys(erros).length > 0;

  return (
    <RecrutamentoModal
      open={open}
      title={TITULOS[tipo]}
      description={`${candidato} — etapa atual: ${etapaAtual}${etapaDestino ? ` → ${etapaDestino}` : ''}`}
      submitLabel={TITULOS[tipo]}
      submitVariant={tipo === 'reprovar' ? 'danger' : 'primary'}
      isSubmitting={loading}
      submitError={error}
      canSubmit={!invalido}
      onClose={onCancel}
      onSubmit={() => {
        setTouched(true);
        if (invalido) return;
        onConfirm({ parecer: parecer.trim() || undefined, score: notaNumerica });
      }}
    >
      <Textarea
        label={parecerObrigatorio ? 'Parecer (obrigatório)' : 'Parecer (opcional)'}
        rows={4}
        value={parecer}
        onChange={(event) => setParecer(event.target.value)}
        onBlur={() => setTouched(true)}
        error={touched && erros.parecer ? erros.parecer : undefined}
      />

      {ACEITA_NOTA.includes(tipo) && (
        <TextInput
          label="Nota da etapa (0 a 10, opcional)"
          type="number"
          min={0}
          max={10}
          step="0.5"
          value={score}
          onChange={(event) => setScore(event.target.value)}
          error={erros.score}
        />
      )}
    </RecrutamentoModal>
  );
}
